/**
 * 失败页（`FJFailedPage`）：对外 action 入口。
 * 统一通过 `FJFailedPageAction` 访问。
 */
import FJLongToShortStore from '@src/store/FJLongToShortStore';
import type { AppThunk } from '../appThunk';
import { FJ_COMMON_VIEW, setActiveView } from '../slice/FJCommonSlice';
import { FJStartLandingAction } from './FJStartLandingAction';
import { setGeneratingOverallProgress } from '../slice/FJGeneratingPageSlice';
import type { ProjectItem } from '@src/types/FJProject';
import { GENERATE_STATE } from '@src/constants/FJEnumConstant';
import { FJCommonAction } from './FJCommonAction';
import { FJSettingPageAction } from './FJSettingPageAction';
import {
    buildLongToShortsGenerateFailEaFromTaskCallback,
    eaLogLongToShortsGenerateFail,
} from '@src/util/FJLongToShortEa';
import FJRequirementsUtil from '@src/util/FJRequirementsUtil';

export const FJFailedPageAction = {
    showFailedPageAction: (): AppThunk => (dispatch, getState) => {
        void getState();
        dispatch(setActiveView(FJ_COMMON_VIEW.FAILED));
    },
    hideFailedPageBackAction: (): AppThunk => (dispatch, getState) => {
        const failedEventId = FJLongToShortStore.getCurFailedTaskId();
        if (failedEventId) {
            const row = getState().startLanding.projects.items.find(i => i.eventId === failedEventId);
            if (row?.status === GENERATE_STATE.PROCESSING) {
                dispatch(
                    FJStartLandingAction.syncProjectGenerateFailedAction({
                        eventId: failedEventId,
                        errorCode: FJLongToShortStore.getCurFailedErrorCode(),
                    }),
                );
            }
        }
        dispatch(setActiveView(FJ_COMMON_VIEW.START_LANDING));
        FJRequirementsUtil.eaLog('long_to_shorts_generate_fail_back_click');
    },

    hideFailedPageTryAgainAction: (): AppThunk => async (dispatch, getState) => {
        const { totalCredits, freeTimes } = getState().common;
        const curFailedTaskId = FJLongToShortStore.getCurFailedTaskId();
        if (!curFailedTaskId) {
            // 没有失败任务，重新生成
            dispatch(FJSettingPageAction.generateAction());
            return;
        }
        FJRequirementsUtil.eaLog('long_to_shorts_generate_fail_try_again_click', {
            project_id: curFailedTaskId,
        });
        const item = getState().startLanding.projects.items.find(i => i.eventId === curFailedTaskId);
        if (!item) {
            return;
        }
        if (item.status === GENERATE_STATE.PENDING) {
            dispatch(FJStartLandingAction.resumePendingAction(item.id));
            return;
        }
        const duration = item.duration;
        const canGenerate = FJLongToShortStore.beforeCheck(totalCredits, freeTimes, duration);
        if (!canGenerate) {
            return;
        }

        dispatch(setActiveView(FJ_COMMON_VIEW.GENERATING));
        FJLongToShortStore.setCurFailedTaskId(null);
        await FJLongToShortStore.regenerateTask({
            eventId: curFailedTaskId,
            progressCallback: ({ id, progress }: { id: string; progress: number }) => {
                if (id === curFailedTaskId) {
                    const p = Math.min(100, progress * 100);
                    dispatch(setGeneratingOverallProgress(p));
                    dispatch(FJStartLandingAction.updataProjectProgressAction(id, p));
                }
            },
            callback: (item: ProjectItem) => {
                if (item.status === GENERATE_STATE.PENDING) {
                    dispatch(FJStartLandingAction.runPendingAction(item));
                } else {
                    eaLogLongToShortsGenerateFail(
                        buildLongToShortsGenerateFailEaFromTaskCallback(item, curFailedTaskId ?? ''),
                    );
                    dispatch(
                        FJStartLandingAction.syncProjectGenerateFailedAction({
                            eventId: curFailedTaskId ?? item?.eventId ?? '',
                            errorCode: item?.errorCode,
                            taskItem: item,
                        }),
                    );
                    dispatch(FJFailedPageAction.showFailedPageAction());
                    FJLongToShortStore.setCurFailedTaskId(
                        curFailedTaskId || item?.eventId,
                        item?.errorCode ?? FJLongToShortStore.getCurFailedErrorCode(),
                    );
                }
                dispatch(FJCommonAction.fetchTaskListAction());
                dispatch(FJCommonAction.updateTotalCreditsAction());
            },
        });
    },
} as const;
